<!--
  Admin mode › Flexi › Settings › Exposure assessment templates › one template.
  Stacked cards: metadata, one card per section, the assigned businesses.
-->
<div class="fvr-editor" [attr.aria-busy]="loading || busy">
  <header class="fvr-head fvr-editor__head">
    <div class="fvr-editor__heading">
      <a class="fvr-back" href="javascript:void(0)" (click)="backToLibrary()">
        <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.LIBRARY.TITLE' | translate }}
      </a>
      @if (template; as t) {
        <div class="fvr-editor__title-row">
          <h2 class="fvr-title">{{ name || t.Name }}</h2>
          <span class="fvr-pill" [class.fvr-pill--done]="isPublished" [class.fvr-pill--draft]="t.Status === TemplateStatus.Draft">
            {{ 'FLEXI_EXPOSURE.TEMPLATE_STATUS.' + t.Status | translate }}
            @if (t.Version > 0) {
              {{ 'FLEXI_EXPOSURE.VERSION' | translate: { version: t.Version } }}
            }
          </span>
          @if (isDirty && !isArchived) {
            <span class="fvr-pill fvr-pill--draft">{{ 'FLEXI_EXPOSURE.EDITOR.UNSAVED' | translate }}</span>
          }
        </div>
        <p class="fvr-subtitle">
          {{ (isArchived ? 'FLEXI_EXPOSURE.EDITOR.ARCHIVED_NOTE' : 'FLEXI_EXPOSURE.EDITOR.SNAPSHOT_NOTE') | translate: { version: t.Version } }}
        </p>
      }
    </div>

    @if (template && !isArchived) {
      <div class="fvr-editor__actions">
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="loading || busy" (click)="confirmArchive()">
          @if (archiving) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (archiving ? 'FLEXI_EXPOSURE.FEEDBACK.ARCHIVING' : 'FLEXI_EXPOSURE.EDITOR.ARCHIVE') | translate }}
        </button>
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="loading || busy || !isDirty" (click)="save()">
          @if (saving) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (saving ? 'FLEXI_EXPOSURE.SAVING' : 'FLEXI_EXPOSURE.SAVE') | translate }}
        </button>
        <button type="button" class="fas-btn fas-btn--primary" [disabled]="loading || busy || !factorCount" (click)="publish()">
          @if (publishing) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (publishing ? 'FLEXI_EXPOSURE.FEEDBACK.PUBLISHING' : 'FLEXI_EXPOSURE.EDITOR.PUBLISH') | translate: { version: (template?.Version ?? 0) + 1 } }}
        </button>
      </div>
    }
  </header>

  @if (submitted && invalidKey) {
    <p class="fas-error" role="alert">{{ invalidKey | translate }}</p>
  }

  @if (loading) {
    <flexi-safety-round-skeleton />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_EXPOSURE.LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_EXPOSURE.RETRY' | translate }}</button>
  }

  @if (busy) {
    <p class="fvr-action-status" role="status">{{ 'FLEXI_EXPOSURE.FEEDBACK.WORKING' | translate }}</p>
  }
  <div class="fvr-operation-content" [attr.inert]="busy ? '' : null">
  @if (template && !loading) {
    <!-- ═══════════ About the template ═══════════ -->
    <section class="fvr-card fvr-editor__card" aria-labelledby="fer-meta-heading">
      <h3 class="fvr-h3" id="fer-meta-heading">{{ 'FLEXI_EXPOSURE.EDITOR.ABOUT' | translate }}</h3>
      <div class="fvr-field">
        <label for="fer-ed-name">{{ 'FLEXI_EXPOSURE.EDITOR.NAME' | translate }} <span class="fvr-required">*</span></label>
        <input pInputText id="fer-ed-name" type="text" maxlength="200" [(ngModel)]="name" [disabled]="isArchived" />
      </div>
      <div class="fvr-field">
        <label for="fer-ed-description">{{ 'FLEXI_EXPOSURE.EDITOR.DESCRIPTION' | translate }}</label>
        <textarea pTextarea id="fer-ed-description" rows="2" maxlength="1000" [(ngModel)]="description" [disabled]="isArchived"></textarea>
      </div>
      <div class="fvr-editor__default">
        <button
          type="button"
          role="switch"
          class="fvr-switch"
          id="fer-ed-default"
          [attr.aria-checked]="template.IsDefaultForNewBusinesses"
          [disabled]="!isPublished || togglingDefault"
          (click)="toggleDefault()"
        ></button>
        <label for="fer-ed-default">
          <strong>{{ 'FLEXI_EXPOSURE.LIBRARY.COLUMN_DEFAULT' | translate }}</strong>
          @if (!isPublished) {
            <span class="fas-muted"> · {{ 'FLEXI_EXPOSURE.LIBRARY.DEFAULT_NEEDS_PUBLISHED' | translate }}</span>
          }
        </label>
      </div>
    </section>

    <!-- ═══════════ Sections ═══════════ -->
    @for (section of sections; track $index; let s = $index; let first = $first; let last = $last) {
      <section class="fvr-card fvr-editor__card fvr-area" [class.fvr-area--open]="openSection === s">
        <div class="fvr-area__head">
          @if (renamingSection === s) {
            <form class="fvr-area__rename" (ngSubmit)="commitRename()">
              <label class="fas-sr-only" [for]="'fer-section-name-' + s">{{ 'FLEXI_EXPOSURE.EDITOR.SECTION_NAME' | translate }}</label>
              <input pInputText [id]="'fer-section-name-' + s" name="sectionName" type="text" maxlength="200" [(ngModel)]="renameValue" />
              <button type="submit" class="fas-btn fas-btn--primary">{{ 'FLEXI_EXPOSURE.EDITOR.OK' | translate }}</button>
              <button type="button" class="fas-btn fas-btn--ghost" (click)="cancelRename()">{{ 'CANCEL' | translate }}</button>
            </form>
          } @else {
            <button type="button" class="fvr-area__toggle" [attr.aria-expanded]="openSection === s" (click)="toggleSection(s)">
              <i class="pi" [ngClass]="openSection === s ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
              <span class="fvr-area__name">{{ s + 1 }}. {{ section.name }}</span>
              <span class="fas-muted">· {{ 'FLEXI_EXPOSURE.EDITOR.SECTION_COUNT' | translate: { count: section.factors.length } }}</span>
            </button>
          }
          @if (!isArchived && renamingSection !== s) {
            <div class="fvr-area__tools">
              <button type="button" class="fas-icon-btn" [disabled]="first || !!editing" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.MOVE_SECTION_UP' | translate" (click)="moveSection(s, -1)">
                <i class="pi pi-arrow-up" aria-hidden="true"></i>
              </button>
              <button type="button" class="fas-icon-btn" [disabled]="last || !!editing" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.MOVE_SECTION_DOWN' | translate" (click)="moveSection(s, 1)">
                <i class="pi pi-arrow-down" aria-hidden="true"></i>
              </button>
              <button type="button" class="fas-btn fas-btn--text" (click)="startRename(s)">{{ 'FLEXI_EXPOSURE.EDITOR.RENAME' | translate }}</button>
              <button type="button" class="fas-icon-btn" [disabled]="!!editing" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.REMOVE_SECTION' | translate" (click)="removeSection(s)">
                <i class="pi pi-trash" aria-hidden="true"></i>
              </button>
            </div>
          }
        </div>

        @if (openSection === s) {
          <ol class="fvr-area__items">
            @for (factor of section.factors; track $index; let i = $index) {
              <li>
                @if (isEditing(s, i) && editing; as edit) {
                  <div class="fvr-cp-edit">
                    <div class="fvr-cp-edit__title">
                      <span class="fvr-cp__num">{{ i + 1 }}</span>
                      <div class="fvr-field fvr-cp-edit__title-field">
                        <label [for]="'fer-factor-title-' + s + '-' + i">{{ 'FLEXI_EXPOSURE.EDITOR.FACTOR' | translate }} <span class="fvr-required">*</span></label>
                        <input pInputText [id]="'fer-factor-title-' + s + '-' + i" type="text" maxlength="300" [(ngModel)]="edit.title" />
                      </div>
                      <button type="button" class="fas-icon-btn" [disabled]="i === 0" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.MOVE_UP' | translate" (click)="moveEditedFactor(-1)">
                        <i class="pi pi-arrow-up" aria-hidden="true"></i>
                      </button>
                      <button type="button" class="fas-icon-btn" [disabled]="i === section.factors.length - 1" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.MOVE_DOWN' | translate" (click)="moveEditedFactor(1)">
                        <i class="pi pi-arrow-down" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div class="fvr-field">
                      <label [for]="'fer-factor-guide-' + s + '-' + i">{{ 'FLEXI_EXPOSURE.ASSESS.GUIDANCE' | translate }}</label>
                      <textarea
                        pTextarea
                        [id]="'fer-factor-guide-' + s + '-' + i"
                        rows="6"
                        maxlength="8000"
                        [attr.aria-describedby]="'fer-factor-guide-help-' + s + '-' + i"
                        [(ngModel)]="edit.guidance"
                      ></textarea>
                      <span class="fvr-hint" [id]="'fer-factor-guide-help-' + s + '-' + i">{{ 'FLEXI_EXPOSURE.EDITOR.GUIDANCE_HINT' | translate }}</span>
                    </div>
                    <div class="fvr-field">
                      <span class="fvr-label">{{ 'FLEXI_EXPOSURE.ASSESS.LEGAL' | translate }}</span>
                      @for (ref of edit.references; track $index; let r = $index) {
                        <div class="fvr-cp-edit__ref">
                          <input
                            pInputText
                            type="text"
                            maxlength="400"
                            [(ngModel)]="ref.Title"
                            [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.REFERENCE_TITLE' | translate: { index: r + 1 }"
                            [placeholder]="'FLEXI_EXPOSURE.EDITOR.REFERENCE_TITLE_PLACEHOLDER' | translate"
                          />
                          <input
                            pInputText
                            type="url"
                            maxlength="1000"
                            [(ngModel)]="ref.Url"
                            [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.REFERENCE_URL' | translate: { index: r + 1 }"
                            placeholder="https://lovdata.no/..."
                          />
                          <button type="button" class="fas-icon-btn" [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.REMOVE_REFERENCE' | translate" (click)="removeReference(r)">
                            <i class="pi pi-times" aria-hidden="true"></i>
                          </button>
                        </div>
                      }
                      <div>
                        <button type="button" class="fas-btn fas-btn--text" (click)="addReference()">
                          <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.EDITOR.ADD_REFERENCE' | translate }}
                        </button>
                      </div>
                    </div>
                    <div class="fvr-cp-edit__foot">
                      <span></span>
                      <div class="fvr-cp-edit__buttons">
                        <button type="button" class="fas-btn fas-btn--text fvr-danger" (click)="removeEditedFactor()">{{ 'FLEXI_EXPOSURE.EDITOR.DELETE' | translate }}</button>
                        <button type="button" class="fas-btn fas-btn--ghost" (click)="cancelEdit()">{{ 'CANCEL' | translate }}</button>
                        <button type="button" class="fas-btn fas-btn--primary" [disabled]="!edit.title.trim()" (click)="applyEdit()">
                          {{ 'FLEXI_EXPOSURE.EDITOR.SAVE_FACTOR' | translate }}
                        </button>
                      </div>
                    </div>
                  </div>
                } @else {
                  <div class="fvr-cp">
                    <span class="fvr-cp__num">{{ i + 1 }}</span>
                    <div class="fvr-cp__text">
                      <strong>{{ factor.Title }}</strong>
                      @if (factorMeta(factor); as meta) {
                        <span class="fas-muted">{{ meta }}</span>
                      }
                    </div>
                    @if (!isArchived) {
                      <button type="button" class="fas-btn fas-btn--text" [disabled]="!!editing" (click)="editFactor(s, i)">
                        {{ 'FLEXI_EXPOSURE.EDITOR.EDIT' | translate }}
                      </button>
                    }
                  </div>
                }
              </li>
            } @empty {
              <li class="fas-muted">{{ 'FLEXI_EXPOSURE.EDITOR.SECTION_EMPTY' | translate }}</li>
            }
          </ol>
          @if (!isArchived) {
            <div>
              <button type="button" class="fas-btn fas-btn--text" [disabled]="!!editing" (click)="addFactor(s)">
                <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.EDITOR.ADD_FACTOR' | translate }}
              </button>
            </div>
          }
        }
      </section>
    }

    @if (!isArchived) {
      <div>
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="!!editing" (click)="addSection()">
          <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_EXPOSURE.EDITOR.ADD_SECTION' | translate }}
        </button>
      </div>
    }

    <!-- ═══════════ Assigned businesses ═══════════ -->
    <section class="fvr-card fvr-editor__card" aria-labelledby="fer-assigned-heading">
      <div class="fvr-head">
        <div>
          <h3 class="fvr-h3" id="fer-assigned-heading">{{ 'FLEXI_EXPOSURE.EDITOR.ASSIGNED' | translate }}</h3>
          <span class="fas-muted" *ngIf="!assignmentsLoading">
            {{ 'FLEXI_EXPOSURE.EDITOR.ASSIGNED_SUMMARY' | translate: { count: assignments.length, defaults: assignedDefaultCount } }}
          </span>
        </div>
        <button
          type="button"
          class="fas-btn fas-btn--ghost"
          [disabled]="!isPublished"
          [pTooltip]="!isPublished ? ('FLEXI_EXPOSURE.EDITOR.ASSIGN_NEEDS_PUBLISHED' | translate) : ''"
          (click)="openAssign()"
        >
          {{ 'FLEXI_EXPOSURE.EDITOR.PICK_BUSINESSES' | translate }}
        </button>
      </div>
      @if (assignmentsLoading) { <flexi-safety-round-skeleton /> }
      <div class="fvr-table-card" *ngIf="!assignmentsLoading">
        <p-table
          [value]="assignments"
          dataKey="BusinessId"
          [rows]="10"
          [paginator]="assignments.length > 10"
          styleClass="p-datatable-sm"
        >
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'FLEXI_EXPOSURE.EDITOR.COLUMN_BUSINESS' | translate }}</th>
              <th>{{ 'FLEXI_EXPOSURE.EDITOR.COLUMN_ASSIGNED_ON' | translate }}</th>
              <th>{{ 'FLEXI_EXPOSURE.EDITOR.COLUMN_DEFAULT' | translate }}</th>
              <th class="fvr-editor__action-col"><span class="fas-sr-only">{{ 'ACTIONS' | translate }}</span></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.BusinessName }}</td>
              <td class="fvr-num">{{ row.AssignedOn | date: 'dd-MM-yyyy' }}</td>
              <td>{{ (row.IsDefault ? 'FLEXI_EXPOSURE.YES' : 'FLEXI_EXPOSURE.NO') | translate }}</td>
              <td class="fvr-editor__action-col">
                <button
                  type="button"
                  class="fas-btn fas-btn--text"
                  [disabled]="removingIds.has(row.BusinessId)"
                  [attr.aria-label]="'FLEXI_EXPOSURE.EDITOR.REMOVE_ARIA' | translate: { name: row.BusinessName }"
                  (click)="confirmUnassign(row)"
                >
                  {{ (removingIds.has(row.BusinessId) ? 'FLEXI_EXPOSURE.FEEDBACK.UPDATING' : 'FLEXI_EXPOSURE.EDITOR.REMOVE') | translate }}
                </button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="fas-muted">{{ 'FLEXI_EXPOSURE.EDITOR.NO_ASSIGNMENTS' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </section>
  }
  </div>
</div>

@if (assignOpen && template) {
  <flexi-exposure-assign-drawer
    [templateId]="template.Id"
    [templateName]="template.Name"
    (closed)="assignClosed($event)"
  ></flexi-exposure-assign-drawer>
}

<p-confirmDialog key="ferEditor" [style]="{ width: '480px', maxWidth: '95vw' }"></p-confirmDialog>
